/**
 * useCourse.js
 * Custom hook that fetches a single course from the backend API by slug.
 *
 * Falls back to the local static data if the API is unreachable (offline dev),
 * so the internship pages continue to work without a running backend.
 *
 * Returns: { courseData, loading, error }
 *   - courseData:  The course object with all fields, or null if not yet loaded
 *   - loading:     true while the fetch is in-progress
 *   - error:       error message string if fetch failed
 */

import { useState, useEffect } from 'react';
import { resolveIcon } from '../utils/iconMap';
// Static data kept as offline fallback — NOT the live source of truth
import staticCoursesData from '../data';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

/**
 * Transforms a raw MongoDB course document so it is compatible with
 * InternshipDetailPage.jsx — specifically, it resolves iconKey strings
 * back to React elements for programHighlights.features and whoShouldApply.
 *
 * @param {Object} raw - Course document from API
 * @returns {Object} - Transformed course data ready for the page
 */
function transformCourseData(raw) {
  return {
    ...raw,
    // Resolve icon keys in programHighlights features
    programHighlights: raw.programHighlights
      ? {
          ...raw.programHighlights,
          features: (raw.programHighlights.features || []).map((f) => ({
            ...f,
            icon: resolveIcon(f.iconKey),
          })),
        }
      : raw.programHighlights,
    // Resolve icon keys in whoShouldApply
    whoShouldApply: (raw.whoShouldApply || []).map((w) => ({
      ...w,
      icon: resolveIcon(w.iconKey),
    })),
    // imageUrl from toolsCovered maps to the `image` property used by InternshipDetailPage
    toolsCovered: (raw.toolsCovered || []).map((t) => {
      let img = t.imageUrl || null;
      if (img && img.startsWith('/')) {
        img = `${API_URL}${img}`;
      }
      return {
        ...t,
        image: img,
      };
    }),
  };
}

/**
 * useCourse hook
 *
 * @param {string} slug - Course slug (from :courseType URL param)
 * @returns {{ courseData: Object|null, loading: boolean, error: string|null }}
 */
function useCourse(slug) {
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchCourse = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API_URL}/api/courses/${slug}`);

        if (!res.ok) {
          throw new Error(`API returned ${res.status}`);
        }

        const json = await res.json();

        if (!cancelled && json.success && json.data) {
          setCourseData(transformCourseData(json.data));
        } else if (!cancelled) {
          throw new Error(json.message || 'Course not found');
        }
      } catch (err) {
        if (!cancelled) {
          // --- Graceful fallback to static data for offline / dev use ---
          console.warn(`[useCourse] API failed, falling back to static data: ${err.message}`);
          const staticCourse = staticCoursesData[slug];
          if (staticCourse) {
            setCourseData(staticCourse);
          } else {
            setError('Course not found');
            setCourseData(null);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCourse();

    // Cleanup: prevent state updates if the component unmounts mid-fetch
    return () => { cancelled = true; };
  }, [slug]);

  return { courseData, loading, error };
}

export default useCourse;
